import * as React from 'react';
import gettext from '@sdk/lang/gettext';
import MoreOptionsMenu from '@sdk/components/ui/MoreOptionsMenu';
import Checkbox from '@sdk/components/forms/Checkbox';

import AttachmentSvg from '@web/assets/svg/UI/attachment.svg';
import EyeSvg from '@web/assets/svg/UI/eye.svg';
import { Announcement } from '@web/announcements/types/Announcement';
import { useAnnouncementsUtils } from '@web/announcements/hooks/useAnnouncementUtils';

import {
  Actions,
  Author,
  CheckboxWrapper,
  Info,
  Meta,
  Title,
  Wrapper,
  ViewsButton,
} from './announcementItem.styles';

type Props = {
  item: Announcement;
  isSelected?: boolean;
  onClick: (id: number) => void;
  onDelete: (id: number) => void;
  onSelect?: (id: number) => void;
  onShowViews: (id: number) => void;
};

export const AnnouncementItem = ({
  isSelected,
  item,
  onClick,
  onDelete,
  onSelect,
  onShowViews,
}: Props) => {
  const { checkIfCanManage, dateFormatter } = useAnnouncementsUtils();
  const canManage = checkIfCanManage(item.author.id);
  const postedAt = dateFormatter(item.postedAt);

  return (
    <Wrapper
      role="button"
      isManageable={canManage}
      isSelected={isSelected}
      onClick={() => onClick(item.id)}
      tabIndex={0}>
      {canManage && (
        <CheckboxWrapper>
          <Checkbox onChange={() => onSelect?.(item.id)} value={isSelected} />
        </CheckboxWrapper>
      )}

      <Info>
        <Title semiBold>{item.title}</Title>
        <Author>{item.author.fullname}</Author>
      </Info>

      <Meta>
        {item.attachmentsCount > 0 && (
          <li data-testid="announcement-attachments">
            <AttachmentSvg />
            {item.attachmentsCount}
          </li>
        )}
        {canManage && (
          <li data-testid="announcement-recepients">
            <ViewsButton
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onShowViews(item.id);
              }}>
              <EyeSvg />
              {item.recipients.read}/{item.recipients.total}
            </ViewsButton>
          </li>
        )}
        <li>
          <time data-testid="announcement-date">{postedAt}</time>
        </li>
      </Meta>

      {canManage && (
        <Actions>
          <MoreOptionsMenu
            noBorder
            onClick={(option) => {
              if (option.value === 'view') {
                onClick(item.id);
              }
              if (option.value === 'delete') {
                onDelete(item.id);
              }
            }}
            options={[
              { key: 'view', value: 'view', label: gettext('View') },
              { key: 'delete', value: 'delete', label: gettext('Delete') },
            ]}
          />
        </Actions>
      )}
    </Wrapper>
  );
};
